import { z } from 'zod';
import {
  emailSchema,
  idSchema,
  localDateSchema,
  localTimeSchema,
  nameSchema,
  rateSchema,
  urlSchema,
  versionSchema,
} from './common';
import type { ClientNipDuplicate, CompanyRegistrySource, CompanyVatStatus } from './company-registry';
import { quickNoteBodySchema, quickNoteColorSchema } from './quick-notes';
import { taskPrioritySchema, taskStatusSchema, taskTitleSchema } from './work';

/**
 * Działania Copilota (Fala 10, DECISIONS 2026-10-07 „Copilot ma być prawdziwym asystentem”; MVP §10):
 *  - jednoznaczne polecenie osoby → działanie od razu, wynik z linkiem i „Cofnij” (archiwizacja utworzonego rekordu
 *    albo przywrócenie poprzednich wartości zadania),
 *  - brak danych, niejednoznaczność (np. dwóch podobnych klientów), dane firmy po NIP, stawka spoza polecenia albo dane
 *    klienta w rozmowie (możliwe polecenia ukryte w mailu) → PLAN do potwierdzenia jednym kliknięciem; nic nie jest
 *    zapisane do kliknięcia „Wykonaj” (`copilot.executePlan` z UI),
 *  - polecenie złożone („dodaj klienta …, stawka 180 zł, spotkanie jutro o 15:00, zadanie …”) = plan kilku kroków,
 *    kolejne kroki odwołują się do wyników wcześniejszych (`clientStep` = indeks kroku z nowym / wybranym klientem);
 *    wszystkie kroki w JEDNEJ transakcji (błąd jednego = nic nie zapisane),
 *  - idempotencja w obrębie rozmowy: ten sam krok (te same dane po rozwiązaniu odwołań) drugi raz nie tworzy rekordu
 *    (tabela `idempotency_keys`, zakres `copilot:<osoba>:<rozmowa>`).
 * Każdy krok to wywołanie tej samej usługi co w UI (`crm.createClient`, `work.updateTask`, `meetings.create`…),
 * z uprawnieniami osoby; Copilot nigdy nie zmienia kodu, nie widzi sejfu, a mail i faktura idą przez Akceptacje.
 */

export const COPILOT_PLAN_MAX_STEPS = 8;

/** Rodzaje kroków planu. `use_client` = wskazanie istniejącego klienta (bez zapisu) dla kolejnych kroków. */
export const COPILOT_PLAN_ACTIONS = [
  'use_client',
  'create_client',
  'add_contact',
  'create_task',
  'update_task',
  'add_note',
  'create_meeting',
  'add_monitor',
  // 17.10.2026: szybka notatka (karteczka na „Dziś”) — „zanotuj …” / „zapisz na karteczce …”
  'add_quick_note',
] as const;
export type CopilotPlanAction = (typeof COPILOT_PLAN_ACTIONS)[number];

const stepIndexSchema = z
  .number()
  .int()
  .min(0)
  .max(COPILOT_PLAN_MAX_STEPS - 1);

/** Klient kroku: id istniejącego albo indeks wcześniejszego kroku `create_client` / `use_client`. */
const clientRefShape = {
  clientId: idSchema.optional(),
  clientStep: stepIndexSchema.optional(),
};

const useClientStep = z
  .object({ action: z.literal('use_client'), clientId: idSchema, name: z.string().trim().min(1).max(200) })
  .strict();

const createClientStep = z
  .object({
    action: z.literal('create_client'),
    name: nameSchema,
    /** stawka domyślna (PLN/h) — tylko z „Widzi stawki i kwoty” */
    defaultRate: rateSchema.optional(),
    /** NIP (10 cyfr) */
    nip: z
      .string()
      .regex(/^\d{10}$/, { message: 'NIP: 10 cyfr' })
      .optional(),
    /** dane firmy z rejestru (pamięć podręczna po wyszukaniu NIP; wartości bierze serwer, nie przeglądarka) */
    useRegistry: z.boolean().optional(),
    website: urlSchema.optional(),
    notes: z.string().trim().max(5_000).optional(),
  })
  .strict();

const addContactStep = z
  .object({
    action: z.literal('add_contact'),
    ...clientRefShape,
    name: nameSchema,
    email: emailSchema.optional(),
    phone: z.string().trim().max(40).optional(),
    role: z.string().trim().max(100).optional(),
  })
  .strict();

const createTaskStep = z
  .object({
    action: z.literal('create_task'),
    title: taskTitleSchema,
    descriptionMd: z.string().max(10_000).optional(),
    ...clientRefShape,
    projectId: idSchema.optional(),
    assigneeUserId: idSchema.optional(),
    dueDate: localDateSchema.optional(),
    priority: taskPrioritySchema.optional(),
  })
  .strict();

const updateTaskStep = z
  .object({
    action: z.literal('update_task'),
    taskId: idSchema,
    /** wersja z chwili przygotowania planu (inna = CONFLICT, chyba że wartości są już takie same) */
    version: versionSchema,
    status: taskStatusSchema.optional(),
    dueDate: localDateSchema.nullable().optional(),
    assigneeUserId: idSchema.nullable().optional(),
    priority: taskPrioritySchema.optional(),
  })
  .strict();

const addNoteStep = z
  .object({
    action: z.literal('add_note'),
    kind: z.enum(['note', 'decision']),
    title: z.string().trim().min(1).max(300),
    bodyMd: z.string().max(50_000),
    ...clientRefShape,
    projectId: idSchema.optional(),
  })
  .strict();

const createMeetingStep = z
  .object({
    action: z.literal('create_meeting'),
    title: z.string().trim().min(1).max(300),
    date: localDateSchema,
    start: localTimeSchema,
    end: localTimeSchema.optional(),
    ...clientRefShape,
    location: z.string().trim().max(300).optional(),
  })
  .strict();

const addMonitorStep = z
  .object({
    action: z.literal('add_monitor'),
    url: urlSchema,
    /** zasób (strona) w Utrzymaniu; bez niego: nowy zasób „Strona” klienta z tym adresem */
    assetId: idSchema.optional(),
    ...clientRefShape,
    assetName: z.string().trim().min(1).max(200).optional(),
  })
  .strict();

/** Szybka notatka osoby (prywatna karteczka na „Dziś”, `notes.add`) — bez klienta i projektu. */
const addQuickNoteStep = z
  .object({
    action: z.literal('add_quick_note'),
    body: quickNoteBodySchema,
    color: quickNoteColorSchema.optional(),
    dueDate: localDateSchema.optional(),
  })
  .strict();

export const copilotPlanStepSchema = z.discriminatedUnion('action', [
  useClientStep,
  createClientStep,
  addContactStep,
  createTaskStep,
  updateTaskStep,
  addNoteStep,
  createMeetingStep,
  addMonitorStep,
  addQuickNoteStep,
]);
export type CopilotPlanStep = z.infer<typeof copilotPlanStepSchema>;

/** Odwołania do klienta: najwyżej jedno z `clientId` / `clientStep`, krok wcześniejszy z klientem. */
export function planRefProblems(steps: readonly CopilotPlanStep[]): { index: number; message: string }[] {
  const out: { index: number; message: string }[] = [];
  steps.forEach((s, index) => {
    if (!('clientStep' in s) || s.clientStep === undefined) return;
    if ('clientId' in s && s.clientId) {
      out.push({ index, message: 'Podaj klienta albo krok z klientem, nie oba' });
      return;
    }
    const ref = steps[s.clientStep];
    if (s.clientStep >= index || !ref || (ref.action !== 'create_client' && ref.action !== 'use_client')) {
      out.push({ index, message: 'Krok z klientem musi być wcześniej w planie' });
    }
  });
  return out;
}

export const copilotExecutePlanInput = z
  .object({
    steps: z.array(copilotPlanStepSchema).min(1).max(COPILOT_PLAN_MAX_STEPS),
    /** klucz rozmowy (idempotencja: ten sam krok w tej rozmowie nie tworzy duplikatu) */
    conversationKey: z.string().trim().min(1).max(128).optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    for (const p of planRefProblems(v.steps)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['steps', p.index, 'clientStep'], message: p.message });
    }
  });
export type CopilotExecutePlanInput = z.input<typeof copilotExecutePlanInput>;

// ── Cofnij ──────────────────────────────────────────────────────────────────

const archiveUndo = <K extends string>(kind: K) => z.object({ kind: z.literal(kind), id: idSchema }).strict();

export const copilotUndoStepSchema = z.discriminatedUnion('kind', [
  archiveUndo('archive_client'),
  archiveUndo('archive_contact'),
  archiveUndo('archive_task'),
  archiveUndo('archive_knowledge'),
  archiveUndo('archive_meeting'),
  archiveUndo('archive_monitor'),
  archiveUndo('archive_asset'),
  // Fala 10 (tor notes-history): notatka klienta z narzędzia `add_client_note`
  archiveUndo('archive_client_note'),
  // 17.10.2026: szybka notatka z „zanotuj …” (archiwum, bez usuwania)
  archiveUndo('archive_quick_note'),
  z
    .object({
      kind: z.literal('restore_task'),
      id: idSchema,
      /** wersja po zmianie Copilota: inna = ktoś zmienił zadanie później (CONFLICT, cofnij ręcznie) */
      version: versionSchema,
      before: z
        .object({
          status: taskStatusSchema.optional(),
          dueDate: localDateSchema.nullable().optional(),
          assigneeUserId: idSchema.nullable().optional(),
          priority: taskPrioritySchema.optional(),
        })
        .strict(),
    })
    .strict(),
]);
export type CopilotUndoStep = z.infer<typeof copilotUndoStepSchema>;

/** Kroki w kolejności wykonania (odwrotnej do działań): „Cofnij wszystko” podaje je razem, w jednej transakcji. */
export const copilotUndoInput = z
  .object({
    steps: z
      .array(copilotUndoStepSchema)
      .min(1)
      .max(5 * COPILOT_PLAN_MAX_STEPS),
  })
  .strict();
export type CopilotUndoInput = z.input<typeof copilotUndoInput>;

export interface CopilotUndoResult {
  /** cofnięte kroki (archiwizacja / przywrócenie) */
  undone: number;
  /** kroki bez zmian (np. rekord był już w archiwum) */
  unchanged: number;
  summary: string;
}

// ── Wynik wykonania ─────────────────────────────────────────────────────────

export type CopilotStepStatus = 'done' | 'repeated' | 'unchanged';

export interface CopilotExecutedStep {
  index: number;
  action: CopilotPlanAction;
  status: CopilotStepStatus;
  /** krótki opis po polsku („Dodano klienta Kowalski Design”) */
  label: string;
  link: { type: string; id: string } | null;
  /** kroki „Cofnij” tego działania (puste: nic nie zmieniono albo powtórzenie) */
  undo: CopilotUndoStep[];
  /**
   * 10.10.2026: osoba przypisana do dodanego zadania (`create_task`) — karta w czacie dopisuje „Przypisane do …”,
   * gdy to ktoś inny niż osoba, która zleciła (zadanie jest wtedy w Zadaniach: Zespół albo Zlecone przeze mnie)
   */
  assignee?: { userId: string; name: string } | null;
}

/** Imię do krótkich komunikatów („Przypisane do Marcin”): pierwsze słowo nazwy osoby. */
export function firstNameOf(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}

/** Nazwa listy zadań, które dodałem i przypisałem innym (Zadania → Wykonawca; forma neutralna, 10.10.2026). */
export const DELEGATED_TASKS_LABEL = 'Zlecone przeze mnie';

/** Dopisek pod dodanym zadaniem przypisanym innej osobie (karta planu, zadanie z czatu). */
export function assignedElsewhereText(name: string): string {
  return `Przypisane do ${firstNameOf(name)} — zobaczysz je w Zadaniach (Zespół albo ${DELEGATED_TASKS_LABEL})`;
}

export interface CopilotPlanResult {
  steps: CopilotExecutedStep[];
  summary: string;
}

// ── Podgląd planu (zdarzenie czatu) ─────────────────────────────────────────

/** Dane firmy z rejestru po NIP pokazywane przed zapisem klienta (bez rachunków bankowych). */
export interface ChatPlanCompany {
  nip: string;
  found: boolean;
  legalName: string | null;
  /** adres jednym wierszem */
  address: string | null;
  vatStatus: CompanyVatStatus | null;
  source: CompanyRegistrySource | null;
  simulated: boolean;
}

export interface ChatPlanChoice {
  id: string;
  label: string;
  /** krok po wyborze tej opcji */
  step: CopilotPlanStep;
}

export interface ChatPlanStepPreview {
  index: number;
  action: CopilotPlanAction;
  /** „Nowy klient: Kowalski Design · stawka 180 zł/h” */
  label: string;
  details: string[];
  /** krok gotowy do wykonania; null = czeka na wybór (`choices`) albo odpowiedź (`question`) */
  step: CopilotPlanStep | null;
  choices: ChatPlanChoice[];
  /** czego brakuje (pytanie do osoby — odpowiedź w rozmowie) */
  question: string | null;
  /** ostrzeżenie (np. ten NIP już jest u innego klienta) */
  warning: string | null;
  company?: ChatPlanCompany | null;
  duplicates?: ClientNipDuplicate[];
}

/** Dlaczego plan czeka na potwierdzenie zamiast wykonać się od razu. */
export const CHAT_PLAN_REASONS = ['untrusted', 'choice', 'missing', 'registry', 'rate', 'duplicate'] as const;
export type ChatPlanReason = (typeof CHAT_PLAN_REASONS)[number];

export interface ChatPlanPreview {
  steps: ChatPlanStepPreview[];
  reasons: ChatPlanReason[];
  /** klucz rozmowy do wykonania z UI (podwójne kliknięcie nie tworzy duplikatu) */
  conversationKey: string | null;
}

/**
 * Kroki do wykonania po wyborach osoby (`chosen`: indeks kroku → id wybranej opcji). null = któryś krok czeka jeszcze
 * na wybór albo na odpowiedź w rozmowie.
 */
export function planStepsToExecute(
  plan: Pick<ChatPlanPreview, 'steps'>,
  chosen: Readonly<Record<number, string>>,
): CopilotPlanStep[] | null {
  const out: CopilotPlanStep[] = [];
  for (const s of plan.steps) {
    if (s.question) return null;
    const pick = s.choices.find((c) => c.id === chosen[s.index]);
    const step = pick?.step ?? s.step;
    if (!step) return null;
    out.push(step);
  }
  return out.length ? out : null;
}

export const COPILOT_ACTION_MESSAGES = {
  planNeedsConfirmation: 'Nic nie zostało zapisane — sprawdź plan i kliknij „Wykonaj”.',
  planUntrusted:
    'Rozmowa zawiera dane klienta (np. treść maila), więc działania czekają na Twoje potwierdzenie — sprawdź je.',
  planRate: 'Stawki nie było w Twoim poleceniu — sprawdź ją przed zapisem.',
  planRegistry: 'Dane firmy z rejestru po NIP — sprawdź je przed zapisem klienta.',
  planDuplicate: 'Klient o tej nazwie już jest — wybierz, czy użyć istniejącego, czy dodać nowego.',
  planChoice: 'Wybierz, o co chodzi, i kliknij „Wykonaj”.',
  planMissing: 'Brakuje informacji — odpowiedz w rozmowie, a przygotuję plan ponownie.',
  nothingSaved: 'Nic nie zostało zapisane.',
  /** etykieta zdarzenia planu po „Wykonaj” (karta w czacie zamiast „Do potwierdzenia”) */
  planExecuted: 'Wykonano',
  undone: 'Cofnięto.',
  repeated: 'To już zostało zrobione w tej rozmowie — bez duplikatu.',
  undoConflict: 'Rekord zmienił się od tego czasu — cofnij zmianę ręcznie.',
  rateForbidden: 'Nie masz uprawnienia do stawek — klient zostanie dodany bez stawki albo poproś osobę z uprawnieniem.',
} as const;
